/**
 * Formats a space's start and end dates into a concise, readable duration string.
 *
 * Formatting Rules:
 * - Same day, month, and year: `2026 | Jan 01`
 * - Same year, different dates: `2026 | Jan 01 - May 30`
 * - Different years, same dates: `2026-2027 | Jan 01`
 * - Different years, different dates: `2026-2027 | Jan 01 - May 30`
 * - Start date only: `2026 | Starts May 30`
 * - End date only: `2026 | Ends May 20`
 *
 * @param startDateStr - The ISO string representation of the start date
 * @param endDateStr - The ISO string representation of the end date
 * @returns A specifically formatted duration string for display in UI widgets
 */
const formatSpaceDuration = (
  startDateStr?: string | null,
  endDateStr?: string | null
) => {
  if (!startDateStr && !endDateStr) return '';

  const parseDate = (d: string) => {
    const date = new Date(d);
    // Return early if invalid date
    if (Number.isNaN(date.getTime())) return null;
    return {
      year: date.getFullYear(),
      month: date.toLocaleString('default', { month: 'short' }).substring(0, 3),
      dayPad: String(date.getDate()).padStart(2, '0'),
    };
  };

  const start = startDateStr ? parseDate(startDateStr) : null;
  const end = endDateStr ? parseDate(endDateStr) : null;

  if (start && end) {
    if (start.year === end.year) {
      if (start.month === end.month && start.dayPad === end.dayPad) {
        // Case: Exactly the same day, month, and year
        return `${start.year} | ${start.month} ${start.dayPad}`;
      }
      // Case: Same year, but different days or months
      return `${start.year} | ${start.month} ${start.dayPad} - ${end.month} ${end.dayPad}`;
    }

    if (start.month === end.month && start.dayPad === end.dayPad) {
      // Case: Different years, but exactly the same day and month
      return `${start.year}-${end.year} | ${start.month} ${start.dayPad}`;
    }

    // Case: Different years, and different days or months
    return `${start.year}-${end.year} | ${start.month} ${start.dayPad} - ${end.month} ${end.dayPad}`;
  }

  if (start) {
    // Case: Only the start date is provided
    return `${start.year} | Starts ${start.month} ${start.dayPad}`;
  }

  if (end) {
    // Case: Only the end date is provided
    return `${end.year} | Ends ${end.month} ${end.dayPad}`;
  }

  return '';
};

export default formatSpaceDuration;
